import { useEffect, useRef } from 'react'

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { PaymentSessionResponse } from '@/lib/api/generated/models'
import { deriveSubtotal, reportSubtotalBreach, resolveBuyerFeeLines } from '@/lib/buyer-fee-lines'
import { formatCurrency } from '@/lib/utils'

interface OrderSummaryProps {
  paymentSession: PaymentSessionResponse
}

/** Session amounts are minor units; this harness renders dollars. */
const toDollars = (cents: number) => cents / 100

const OrderSummary = ({ paymentSession }: OrderSummaryProps) => {
  const { orderDetails, totalAmount, currency } = paymentSession

  // FF-3182: fee rows come from the session's configured labels, never from string literals.
  const { lines: feeLines, source } = resolveBuyerFeeLines(paymentSession)

  /*
    With configured labels the subtotal absorbs anything not itemised, so the column sums to the
    charged total. Without them, keep the line-item sum this harness has shown — the derivation
    says nothing about the legacy path, where Subtotal *is* the item sum, so it is not run there.
    Running it anyway would report a "conservation breach" against flat fee fields the column
    never claimed to reconcile: a false alarm, and a loud one.
  */
  const derived = source === 'configured' ? deriveSubtotal(totalAmount, feeLines) : null
  const subtotal = derived
    ? derived.subtotalCents
    : orderDetails.lineItems.reduce((sum, item) => sum + item.totalPrice, 0)

  /*
    FF-3273 — this is a QA harness, so a breach is shown rather than smoothed over. The
    production SDK drops the itemisation and shows the charged total, because a buyer has no use
    for a broken column; a tester does. Rendering the real (negative) subtotal keeps the
    arithmetic on screen self-consistent, and the banner says why it looks wrong — a silent
    $0.00 subtotal would read as a normal result in the very harness built to catch this.
  */
  const shortfallCents = derived?.shortfallCents ?? 0
  const breached = shortfallCents > 0

  /*
    Reported on commit, once per distinct breach. `StrictMode` double-invokes both the render
    body and the mount effect, and React Query re-renders this component on every refetch, so an
    unguarded report would repeat a single server bug indefinitely — the ref is what makes "one
    breach, one error" true of the running harness and not just of the test.
  */
  const reportedBreach = useRef<string | null>(null)
  useEffect(() => {
    if (!breached) {
      reportedBreach.current = null
      return
    }
    const breachKey = `${totalAmount}:${shortfallCents}`
    if (reportedBreach.current === breachKey) return
    reportedBreach.current = breachKey
    reportSubtotalBreach(totalAmount, feeLines, shortfallCents)
  }, [breached, totalAmount, shortfallCents, feeLines])

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="bg-blue-50 text-blue-800 rounded-t-lg">
        <CardTitle>Order Summary</CardTitle>
        <CardDescription>Review your order details below.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 pt-6">
        {breached && (
          <div role="alert" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-semibold">Fee lines do not reconcile</p>
            <p>
              The fee rows exceed the charged total by {formatCurrency(toDollars(shortfallCents), currency)}, so this
              column cannot add up. That is a server-side bug — the API should never send a breakdown that breaches
              conservation. The subtotal below is shown as derived rather than clamped.
            </p>
          </div>
        )}
        {/*
          Per-item amounts render on the legacy path only, where Subtotal *is* the item sum and
          the two cannot disagree. With configured labels the subtotal absorbs unlabelled fees,
          so an itemised price would contradict it on screen — $100.00 of goods above a $107.50
          subtotal re-opens the gap the derivation exists to close. The production SDK order
          summary shows the order contents without per-item amounts for the same reason; mirror it.
        */}
        {orderDetails.lineItems.map((item, index) => (
          <div key={index} className="flex justify-between">
            <span>
              {item.name} (x{item.quantity})
            </span>
            {source === 'legacy' && <span>{formatCurrency(toDollars(item.totalPrice), currency)}</span>}
          </div>
        ))}
        <div className="border-t pt-4">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCurrency(toDollars(subtotal), currency)}</span>
          </div>
          {feeLines.map((line, index) => (
            <div key={`${line.componentType}-${index}`} className="flex justify-between">
              <span>{line.label}</span>
              <span>{formatCurrency(toDollars(line.amountCents), currency)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>{formatCurrency(toDollars(totalAmount), currency)}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <p className="text-sm text-gray-500">Powered by FanFair</p>
      </CardFooter>
    </Card>
  )
}

export default OrderSummary
